import { jsPDF } from "jspdf";
import { Token, Tokens } from "marked";

export const HEADER_HEIGHT = 12;
export const FOOTER_HEIGHT = 15;

export const THEME_COLOR = { r: 52, g: 120, b: 246 };

export const PAGE_MARGIN = {
  top: 10 + HEADER_HEIGHT,
  bottom: 10 + FOOTER_HEIGHT,
  left: 20,
  right: 15
};

export const BRANDING = {
  logoUrl: "https://getspoke.com/enlighten/",
  spokeUrl: "https://getspoke.com/",
  aiDisclaimer: "AI can make mistakes.",
  learnMorePath: "/ai-transparency",
  copyright: "© Copyright 2025 Spoke Network, Inc."
};

export const LINE_HEIGHT = {
  normal: 6,
  small: 6,
  medium: 8,
  large: 9,
  transcript: 4.5
};

export const SPACING = {
  section: 5,
  sectionLarge: 10,
  headerLink: 8,
  headerTitle: 10,
  sectionHeaderMin: 30
};

export const TEXT_WRAP_WIDTH = 170;

export const PDF_COLORS = {
  green: { r: 76, g: 175, b: 80 },
  yellow: { r: 255, g: 179, b: 0 },
  red: { r: 244, g: 67, b: 54 },
  blue: { r: 33, g: 150, b: 243 },
  grey: { r: 117, g: 117, b: 117 },
  sectionLine: { r: 25, g: 118, b: 210 }
};

export const FONT_SIZES = {
  link: 9,
  title: 16,
  sectionHeader: 14,
  subHeader: 12,
  body: 10,
  code: 9
};

export const HEADING_CONFIG: Record<number, { fontSize: number; lineHeight: number }> = {
  1: { fontSize: 13, lineHeight: SPACING.sectionLarge },
  2: { fontSize: 12, lineHeight: LINE_HEIGHT.large },
  3: { fontSize: 11, lineHeight: LINE_HEIGHT.medium },
  4: { fontSize: 10, lineHeight: LINE_HEIGHT.normal },
  5: { fontSize: 10, lineHeight: LINE_HEIGHT.normal },
  6: { fontSize: 10, lineHeight: LINE_HEIGHT.normal }
};

export interface TextSegment {
  text: string;
  bold: boolean;
  italic: boolean;
}

export interface PdfContext {
  doc: jsPDF;
  currentY: number;
  maxY: number;
  pageWidth: number;
}

export interface MarkdownRenderContext {
  doc: jsPDF;
  currentY: number;
  maxY: number;
  baseIndent: number;
}

export const calculateTextBlockOffset = (lineCount: number): number => {
  return lineCount * (LINE_HEIGHT.normal - 2) + SPACING.section;
};

export const getPageDimensions = (doc: jsPDF): { pageWidth: number; pageHeight: number; maxY: number } => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxY = pageHeight - PAGE_MARGIN.bottom;
  return { pageWidth, pageHeight, maxY };
};

export const checkPageBreak = (context: PdfContext, requiredSpace: number = SPACING.sectionHeaderMin): number => {
  if (context.currentY + requiredSpace > context.maxY) {
    context.doc.addPage();
    return PAGE_MARGIN.top;
  }
  return context.currentY;
};

export const setFont = (
  doc: jsPDF,
  size: number,
  style: "normal" | "bold" | "italic" | "bolditalic" = "normal"
): void => {
  doc.setFontSize(size);
  doc.setFont("helvetica", style);
};

export const getFontStyle = (bold: boolean, italic: boolean): "normal" | "bold" | "italic" | "bolditalic" => {
  if (bold && italic) {
    return "bolditalic";
  }
  if (bold) {
    return "bold";
  }
  if (italic) {
    return "italic";
  }
  return "normal";
};

export const capitalizeFirst = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const renderSectionHeader = (context: PdfContext, title: string): number => {
  let currentY = checkPageBreak(context);

  setFont(context.doc, FONT_SIZES.sectionHeader, "bold");
  context.doc.setTextColor(0, 0, 0);
  context.doc.text(title, PAGE_MARGIN.left, currentY);
  currentY += 4;

  context.doc.setDrawColor(PDF_COLORS.sectionLine.r, PDF_COLORS.sectionLine.g, PDF_COLORS.sectionLine.b);
  context.doc.setLineWidth(0.5);
  context.doc.line(PAGE_MARGIN.left, currentY, context.pageWidth - PAGE_MARGIN.right, currentY);

  return currentY + SPACING.sectionLarge;
};

export const renderSubSection = (params: {
  doc: jsPDF;
  title: string;
  content: string;
  currentY: number;
}): number => {
  const { doc, title, content } = params;
  let y = params.currentY;

  setFont(doc, FONT_SIZES.subHeader, "bold");
  doc.text(title, PAGE_MARGIN.left, y);
  y += LINE_HEIGHT.normal;

  setFont(doc, FONT_SIZES.body, "normal");
  const lines = doc.splitTextToSize(content, TEXT_WRAP_WIDTH);
  doc.text(lines, PAGE_MARGIN.left, y);
  return y + calculateTextBlockOffset(lines.length);
};

interface TokenProcessorParams {
  token: Token;
  segments: TextSegment[];
  inheritBold: boolean;
  inheritItalic: boolean;
  processTokens: (tokens: Token[], bold: boolean, italic: boolean) => void;
}

type TokenProcessor = (params: TokenProcessorParams) => void;

const tokenProcessors: Record<string, TokenProcessor> = {
  text: ({ token, segments, inheritBold, inheritItalic, processTokens }) => {
    const textToken = token as Tokens.Text & { tokens?: Token[] };
    if (textToken.tokens && textToken.tokens.length > 0) {
      processTokens(textToken.tokens, inheritBold, inheritItalic);
    } else {
      segments.push({ text: textToken.text, bold: inheritBold, italic: inheritItalic });
    }
  },
  strong: ({ token, segments, inheritItalic, processTokens }) => {
    const strongToken = token as Tokens.Strong;
    if (strongToken.tokens) {
      processTokens(strongToken.tokens, true, inheritItalic);
    } else {
      segments.push({ text: strongToken.text, bold: true, italic: inheritItalic });
    }
  },
  em: ({ token, segments, inheritBold, processTokens }) => {
    const emToken = token as Tokens.Em;
    if (emToken.tokens) {
      processTokens(emToken.tokens, inheritBold, true);
    } else {
      segments.push({ text: emToken.text, bold: inheritBold, italic: true });
    }
  },
  link: ({ token, segments, inheritBold, inheritItalic }) => {
    const linkToken = token as Tokens.Link;
    segments.push({ text: linkToken.text, bold: inheritBold, italic: inheritItalic });
  },
  codespan: ({ token, segments, inheritBold, inheritItalic }) => {
    segments.push({ text: (token as Tokens.Codespan).text, bold: inheritBold, italic: inheritItalic });
  }
};

export const extractTextSegments = (tokens: Token[]): TextSegment[] => {
  const segments: TextSegment[] = [];

  const processTokens = (tokenList: Token[], inheritBold = false, inheritItalic = false): void => {
    tokenList.forEach((token) => {
      const handler = tokenProcessors[token.type];
      if (handler) {
        handler({ token, segments, inheritBold, inheritItalic, processTokens });
        return;
      }

      if ("tokens" in token && Array.isArray(token.tokens)) {
        processTokens(token.tokens, inheritBold, inheritItalic);
        return;
      }

      if ("text" in token && typeof token.text === "string") {
        segments.push({ text: token.text, bold: inheritBold, italic: inheritItalic });
      }
    });
  };

  processTokens(tokens);
  return segments;
};

const calculateSegmentPosition = (
  wrappedLines: string[],
  segments: TextSegment[],
  targetLineIndex: number
): { segmentIndex: number; charOffset: number } => {
  let segmentIndex = 0;
  let charOffset = 0;

  for (let i = 0; i < targetLineIndex; i++) {
    let lineLen = wrappedLines[i].length;
    while (lineLen > 0 && segmentIndex < segments.length) {
      const seg = segments[segmentIndex];
      const availableInSeg = seg.text.length - charOffset;
      if (lineLen >= availableInSeg) {
        lineLen -= availableInSeg;
        segmentIndex++;
        charOffset = 0;
      } else {
        charOffset += lineLen;
        lineLen = 0;
      }
    }
  }

  return { segmentIndex, charOffset };
};

interface RenderWrappedLineParams {
  doc: jsPDF;
  remainingLine: string;
  segments: TextSegment[];
  startSegmentIndex: number;
  startCharOffset: number;
  startX: number;
  y: number;
}

const renderWrappedLine = (params: RenderWrappedLineParams): number => {
  const { doc, remainingLine, segments, startSegmentIndex, startCharOffset, startX, y } = params;
  let currentX = startX;
  let segmentIndex = startSegmentIndex;
  let charOffset = startCharOffset;
  let line = remainingLine;

  while (line.length > 0 && segmentIndex < segments.length) {
    const seg = segments[segmentIndex];
    const segTextRemaining = seg.text.slice(charOffset);
    const charsToRender = Math.min(segTextRemaining.length, line.length);
    const textToRender = segTextRemaining.slice(0, charsToRender);

    const fontStyle = getFontStyle(seg.bold, seg.italic);
    setFont(doc, FONT_SIZES.body, fontStyle);
    doc.text(textToRender, currentX, y);
    currentX += doc.getTextWidth(textToRender);

    line = line.slice(charsToRender);
    charOffset += charsToRender;

    if (charOffset >= seg.text.length) {
      segmentIndex++;
      charOffset = 0;
    }
  }

  return currentX;
};

export const renderInlineTokens = (params: {
  doc: jsPDF;
  tokens: Token[];
  x: number;
  y: number;
  maxWidth: number;
  lineHeight: number;
}): number => {
  const { doc, tokens, x, y, maxWidth, lineHeight } = params;
  let currentX = x;
  let currentY = y;

  const segments = extractTextSegments(tokens);
  if (segments.length === 0) {
    return currentY + lineHeight;
  }

  const fullText = segments.map(s => s.text).join("");
  setFont(doc, FONT_SIZES.body, "normal");
  const wrappedLines = doc.splitTextToSize(fullText, maxWidth);

  wrappedLines.forEach((wrappedLine: string, lineIndex: number) => {
    if (lineIndex > 0) {
      currentY += lineHeight;
      currentX = x;
    }

    const { segmentIndex, charOffset } = calculateSegmentPosition(wrappedLines, segments, lineIndex);
    currentX = renderWrappedLine({
      doc,
      remainingLine: wrappedLine,
      segments,
      startSegmentIndex: segmentIndex,
      startCharOffset: charOffset,
      startX: currentX,
      y: currentY
    });
  });

  return currentY + lineHeight;
};

const getListItemTokens = (item: Tokens.ListItem): Token[] => {
  if (!item.tokens || item.tokens.length === 0) {
    return [];
  }

  const firstToken = item.tokens[0];

  if (firstToken.type === "paragraph") {
    return (firstToken as Tokens.Paragraph).tokens ?? [];
  }

  if (firstToken.type === "text" && "tokens" in firstToken && Array.isArray(firstToken.tokens)) {
    return firstToken.tokens;
  }

  return item.tokens.filter(t => t.type !== "list");
};

const drawBullet = (params: {
  doc: jsPDF;
  x: number;
  y: number;
  depth: number;
}): void => {
  const { doc, x, y, depth } = params;
  const bulletY = y - 1.2;
  const radius = 0.6;

  doc.setDrawColor(0, 0, 0);
  doc.setFillColor(0, 0, 0);

  const fillStyle = depth === 0 ? "F" : "S";
  if (depth !== 0) {
    doc.setLineWidth(0.3);
  }
  doc.circle(x, bulletY, radius, fillStyle);
};

export const renderList = (params: {
  doc: jsPDF;
  list: Tokens.List;
  currentY: number;
  maxY: number;
  indent: number;
  depth: number;
}): number => {
  const { doc, list, maxY, indent, depth } = params;
  let { currentY } = params;
  const itemIndent = indent + (depth * 10);
  const textIndent = itemIndent + 6;

  list.items.forEach((item, index) => {
    if (currentY > maxY) {
      doc.addPage();
      currentY = PAGE_MARGIN.top;
    }

    setFont(doc, FONT_SIZES.body, "normal");

    if (list.ordered) {
      doc.text(`${index + 1}.`, itemIndent, currentY);
    } else {
      drawBullet({ doc, x: itemIndent + 1.2, y: currentY, depth });
    }

    const tokensToRender = getListItemTokens(item);
    const nestedLists = (item.tokens?.filter(t => t.type === "list") ?? []) as Tokens.List[];

    if (tokensToRender.length > 0) {
      currentY = renderInlineTokens({
        doc,
        tokens: tokensToRender,
        x: textIndent,
        y: currentY,
        maxWidth: TEXT_WRAP_WIDTH - (textIndent - PAGE_MARGIN.left),
        lineHeight: LINE_HEIGHT.small
      });
    } else {
      currentY += LINE_HEIGHT.small;
    }

    nestedLists.forEach((nestedList) => {
      currentY = renderList({
        doc,
        list: nestedList,
        currentY,
        maxY,
        indent,
        depth: depth + 1
      });
    });
  });

  return currentY;
};

type MarkdownTokenHandler = (
  token: Token,
  context: MarkdownRenderContext,
  renderTokens: (tokens: Token[], ctx: MarkdownRenderContext) => number
) => number;

const markdownHandlers: Record<string, MarkdownTokenHandler> = {
  heading: (token, context) => {
    const heading = token as Tokens.Heading;
    const config = HEADING_CONFIG[heading.depth] ?? { fontSize: 10, lineHeight: LINE_HEIGHT.normal };

    setFont(context.doc, config.fontSize, "bold");
    context.doc.text(heading.text, context.baseIndent, context.currentY);
    return context.currentY + config.lineHeight;
  },

  paragraph: (token, context) => {
    const para = token as Tokens.Paragraph;
    setFont(context.doc, FONT_SIZES.body, "normal");
    return renderInlineTokens({
      doc: context.doc,
      tokens: para.tokens ?? [],
      x: context.baseIndent,
      y: context.currentY,
      maxWidth: TEXT_WRAP_WIDTH - (context.baseIndent - PAGE_MARGIN.left),
      lineHeight: LINE_HEIGHT.small
    });
  },

  list: (token, context) => {
    const list = token as Tokens.List;
    return renderList({
      doc: context.doc,
      list,
      currentY: context.currentY,
      maxY: context.maxY,
      indent: context.baseIndent,
      depth: 0
    });
  },

  space: (_token, context) => {
    return context.currentY + SPACING.section;
  },

  code: (token, context) => {
    const code = token as Tokens.Code;
    let currentY = context.currentY;

    context.doc.setFontSize(FONT_SIZES.code);
    context.doc.setFont("courier", "normal");
    const codeLines = context.doc.splitTextToSize(code.text, TEXT_WRAP_WIDTH - 10);

    codeLines.forEach((line: string) => {
      if (currentY > context.maxY) {
        context.doc.addPage();
        currentY = PAGE_MARGIN.top;
      }
      context.doc.text(line, context.baseIndent + 5, currentY);
      currentY += LINE_HEIGHT.small;
    });

    context.doc.setFont("helvetica", "normal");
    return currentY + SPACING.section;
  },

  blockquote: (token, context, renderTokens) => {
    const quote = token as Tokens.Blockquote;
    setFont(context.doc, FONT_SIZES.body, "italic");

    let currentY = context.currentY;
    if (quote.tokens) {
      currentY = renderTokens(quote.tokens, {
        ...context,
        currentY,
        baseIndent: context.baseIndent + 10
      });
    }

    context.doc.setFont("helvetica", "normal");
    return currentY;
  }
};

export const renderMarkdownTokens = (tokens: Token[], context: MarkdownRenderContext): number => {
  let { currentY } = context;

  tokens.forEach((token) => {
    if (currentY > context.maxY) {
      context.doc.addPage();
      currentY = PAGE_MARGIN.top;
    }

    const handler = markdownHandlers[token.type];
    if (handler) {
      currentY = handler(token, { ...context, currentY }, renderMarkdownTokens);
    }
  });

  return currentY;
};

const LOGO_HEIGHT = 8.5;
const ORIGINAL_LOGO_WIDTH_PX = 136.75;
const ORIGINAL_LOGO_HEIGHT_PX = 32;
const LOGO_ASPECT_RATIO = ORIGINAL_LOGO_WIDTH_PX / ORIGINAL_LOGO_HEIGHT_PX;
const FOOTER_FONT_SIZE = 8;

export const renderPageHeader = (doc: jsPDF, logoBase64: string): void => {
  const { pageWidth } = getPageDimensions(doc);

  doc.setFillColor(THEME_COLOR.r, THEME_COLOR.g, THEME_COLOR.b);
  doc.rect(0, 0, pageWidth, HEADER_HEIGHT, "F");

  const logoWidth = LOGO_HEIGHT * LOGO_ASPECT_RATIO;
  const logoX = PAGE_MARGIN.left;
  const logoY = (HEADER_HEIGHT - LOGO_HEIGHT) / 2;

  doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, LOGO_HEIGHT);
  doc.link(logoX, logoY, logoWidth, LOGO_HEIGHT, { url: BRANDING.logoUrl });
};

export const renderPageFooter = (doc: jsPDF): void => {
  const { pageWidth, pageHeight } = getPageDimensions(doc);
  const footerY = pageHeight - 12;

  setFont(doc, FOOTER_FONT_SIZE, "normal");
  doc.setTextColor(117, 117, 117);

  const disclaimerText = BRANDING.aiDisclaimer;
  const learnMoreText = " Learn More";
  const disclaimerWidth = doc.getTextWidth(disclaimerText);
  const learnMoreWidth = doc.getTextWidth(learnMoreText);
  const totalDisclaimerWidth = disclaimerWidth + learnMoreWidth;
  const disclaimerX = (pageWidth - totalDisclaimerWidth) / 2;

  doc.text(disclaimerText, disclaimerX, footerY);

  doc.setTextColor(0, 102, 204);
  const learnMoreX = disclaimerX + disclaimerWidth;
  const learnMoreUrl = `${window.location.origin}${BRANDING.learnMorePath}`;
  doc.textWithLink(learnMoreText, learnMoreX, footerY, { url: learnMoreUrl });

  doc.setTextColor(117, 117, 117);
  setFont(doc, FOOTER_FONT_SIZE, "normal");

  const copyrightParts = BRANDING.copyright.split("Spoke Network");
  const beforeSpoke = copyrightParts[0];
  const afterSpoke = copyrightParts[1];
  const spokeText = "Spoke Network";

  const beforeWidth = doc.getTextWidth(beforeSpoke);
  const spokeWidth = doc.getTextWidth(spokeText);
  const afterWidth = doc.getTextWidth(afterSpoke);
  const totalCopyrightWidth = beforeWidth + spokeWidth + afterWidth;
  const copyrightX = (pageWidth - totalCopyrightWidth) / 2;
  const copyrightY = footerY + 5;

  doc.text(beforeSpoke, copyrightX, copyrightY);

  doc.setTextColor(0, 102, 204);
  const spokeX = copyrightX + beforeWidth;
  doc.textWithLink(spokeText, spokeX, copyrightY, { url: BRANDING.spokeUrl });

  doc.setTextColor(117, 117, 117);
  doc.text(afterSpoke, spokeX + spokeWidth, copyrightY);

  doc.setTextColor(0, 0, 0);
};

export const renderHeaderFooterOnAllPages = (doc: jsPDF, logoBase64: string): void => {
  const totalPages = doc.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    renderPageHeader(doc, logoBase64);
    renderPageFooter(doc);
  }
};
